/**
 * Reportes → Documentos (`/reportes/documentos`).
 *
 * Puerto de `Reportes::documentos` + `Reportes::resumen_ventas` +
 * `Reportes_model::get_serie_grouped_like{,_admin}` +
 * `views/menu/reportes/documentos.php` (+ `src/reports/documents.js`).
 *
 * La pantalla del panel anterior tenía un botón con un calendario para elegir el
 * **mes**, el **tipo** (facturas, boletas o ambas) y **las series** (selector
 * múltiple pintado con select2), la tabla *Listado de Documentos* (RUC, razón
 * social, serie, número, fecha, base, IGV, total, producto, precio, cantidad,
 * total y método de pago) y botones de Excel y PDF. El pie de la tabla sumaba
 * base, IGV, total, cantidad y el total de las líneas.
 *
 * Reglas conservadas:
 * - Sin mes elegido se consulta el **mes en curso**, como el original.
 * - Se listan las **líneas** de las facturas, boletas, las ventas de
 *   administración, con las mismas columnas y los mismos filtros de estado
 *   (los comprobantes anulados quedan fuera). Los créditos tienen su reporte propio.
 * - Los importes viajan sin redondear y se suman como en el pie anterior.
 *
 * Diferencias anotadas:
 * - El **mes** se elige con el calendario propio del panel (`CampoFecha`) y las
 *   **series** con botones de encendido/apagado en lugar del `<select multiple>`
 *   con select2; al cambiar el tipo se limpian las series, como antes.
 * - Se añade el atajo de **meses con documentos** (el mes por defecto puede estar
 *   vacío si el histórico de la copia es anterior) y la exportación en **CSV**
 *   servida por la API (antes el Excel se armaba en el navegador).
 * - Se listan también los comprobantes **sin estado de envío** (`estado_api`
 *   nulo): la consulta anterior los dejaba fuera de casualidad, porque comparaba
 *   `estado_api != 'ANULADO'` y en SQL un nulo no cumple esa condición. Son ventas
 *   reales no enviadas (en esta copia, las boletas de agosto).
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosDocumentos,
  useDocumentos,
  type FiltrosDocumentos,
  type LineaDocumento,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mes en curso como `AAAA-MM` (el valor por defecto del panel anterior). */
function mesActual(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Tono del badge según el origen del comprobante. */
function tonoDelOrigen(origen: LineaDocumento['origen']): 'brand' | 'neutral' | 'warning' {
  if (origen === 'FACTURA') return 'brand';
  if (origen === 'BOLETA') return 'neutral';

  return 'warning';
}

/** Nombre corto del origen, como se lee en la tabla. */
function nombreDelOrigen(origen: LineaDocumento['origen']): string {
  if (origen === 'ADMINISTRACION') return 'ADMIN.';

  return origen;
}

export function ReporteDocumentosPage() {
  useDocumentTitle('Reportes · Documentos');

  const [filtros, setFiltros] = useState<FiltrosDocumentos>({
    periodo: mesActual(),
    tipo: 'facturaboleta',
    series: [],
  });
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosDocumentos();
  const consulta = useDocumentos(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const meses = catalogos.data?.meses ?? [];
  const ultimoMesConDatos = meses[0]?.periodo ?? null;
  const sinDocumentos = Boolean(
    resumen && resumen.lineas === 0 && ultimoMesConDatos && ultimoMesConDatos !== filtros.periodo,
  );

  /** Series disponibles para el tipo elegido (como el original: F o B). */
  const series = useMemo(() => {
    const listas = catalogos.data?.series;

    if (filtros.tipo === 'factura') return listas?.factura ?? [];
    if (filtros.tipo === 'boleta') return listas?.boleta ?? [];

    return [...(listas?.factura ?? []), ...(listas?.boleta ?? [])];
  }, [catalogos.data, filtros.tipo]);

  const documento = useMemo(() => datos?.documentos ?? [], [datos]);
  // El listado puede traer miles de líneas del mes: se pagina en el navegador y el pie
  // sigue sumando el listado completo.
  const paginacion = usePaginacion(documento, { clave: filtros });

  const cambiar = <C extends keyof FiltrosDocumentos>(campo: C, valor: FiltrosDocumentos[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor }));

  /** Cambiar el tipo limpia las series, como hacía el panel anterior. */
  const cambiarTipo = (tipo: string) =>
    setFiltros((previos) => ({ ...previos, tipo, series: [] }));

  const alternarSerie = (serie: string, marcada: boolean) =>
    setFiltros((previos) => ({
      ...previos,
      series: marcada ? [...previos.series, serie] : previos.series.filter((s) => s !== serie),
    }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarDocumentos(filtros), {
        nombre: `documentos-${filtros.tipo}-${filtros.periodo}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el listado.');
    }
  };

  /** Abre el PDF del reporte (mismo listado, para imprimir o archivar). */
  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfDocumentos(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Documentos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Facturas, boletas y ventas de administración de un mes, línea por línea.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Mes</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="month"
                  value={filtros.periodo}
                  onChange={(evento) => cambiar('periodo', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Tipo</span>
              <Select
                value={filtros.tipo}
                onChange={({ target }) => cambiarTipo(target.value)}
                className={CONTROL}
                aria-label="Tipo de comprobante"
              >
                {(catalogos.data?.tipos ?? []).map((tipo) => (
                  <option key={tipo.valor} value={tipo.valor}>
                    {tipo.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <div className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">
                Series{' '}
                <span className="font-normal text-ink-400">
                  {filtros.series.length === 0
                    ? '(todas las del tipo elegido)'
                    : `(${filtros.series.length} elegida(s))`}
                </span>
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {series.length === 0 ? (
                  <span className="text-xs text-ink-400">Sin series registradas.</span>
                ) : null}

                {series.map((serie) => {
                  const marcada = filtros.series.includes(serie);

                  return (
                    <button
                      key={serie}
                      type="button"
                      aria-pressed={marcada}
                      onClick={() => alternarSerie(serie, !marcada)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        marcada
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                    >
                      {serie}
                    </button>
                  );
                })}

                {filtros.series.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => cambiar('series', [])}
                    className="ml-1 text-xs font-semibold text-ink-500 hover:text-brand-700 hover:underline"
                  >
                    Limpiar
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con documentos
              </span>
              {meses.slice(0, 12).map((mes) => (
                <button
                  key={mes.periodo}
                  type="button"
                  aria-pressed={mes.periodo === filtros.periodo}
                  onClick={() => cambiar('periodo', mes.periodo)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                    mes.periodo === filtros.periodo
                      ? 'border-brand-300 bg-brand-50 text-brand-700'
                      : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                  )}
                  title={`${formatNumber(mes.documentos)} documento(s)`}
                >
                  {etiquetaMes(mes.periodo)}
                </button>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el listado">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      {sinDocumentos ? (
        <Alert variant="info" title="El mes elegido no tiene documentos">
          <p>
            Esta copia tiene documentos entre {etiquetaMes(catalogos.data?.rango.desde)} y{' '}
            {etiquetaMes(catalogos.data?.rango.hasta)}.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => cambiar('periodo', ultimoMesConDatos ?? '')}
          >
            Ver {etiquetaMes(ultimoMesConDatos)}
          </Button>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            etiqueta: 'Documentos',
            valor: formatNumber(resumen?.documentos ?? 0),
            detalle: `${formatNumber(resumen?.lineas ?? 0)} línea(s)`,
          },
          {
            etiqueta: 'Base imponible',
            valor: formatCurrency(resumen?.base ?? 0),
            detalle: `${formatNumber(resumen?.clientes ?? 0)} cliente(s)`,
          },
          {
            etiqueta: 'IGV',
            valor: formatCurrency(resumen?.igv ?? 0),
            detalle: '18 % sobre la base',
          },
          {
            etiqueta: 'Total vendido',
            valor: formatCurrency(resumen?.total ?? 0),
            detalle: `${formatNumber(resumen?.cantidad ?? 0)} unidad(es)`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Listado de documentos ({formatNumber(resumen?.lineas ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {etiquetaMes(datos?.filtros.descripcion ?? filtros.periodo)} ·{' '}
              {filtros.series.length > 0 ? filtros.series.join(', ') : 'todas las series'}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full max-lg:min-w-[46rem] table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[4.75rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[6.25rem] px-1.5 py-2">Comprobante</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Cliente</th>
                    <th className="w-[7rem] px-1.5 py-2">Producto</th>
                    <th className="w-[3.75rem] px-1.5 py-2 text-right">Cant.</th>
                    <th className="w-[6.75rem] px-1.5 py-2 text-right">Total</th>
                    <th className="w-[6rem] px-1.5 py-2">Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {documento.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={7} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay documentos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila, indice) => (
                    <tr
                      key={`${fila.origen}-${fila.comprobante_id}-${indice}`}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900">
                          {formatDateValue(fila.fecha)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-medium text-ink-800">
                          {[fila.serie, fila.numero].filter(Boolean).join('-') || '—'}
                        </p>
                        <Badge
                          variant={tonoDelOrigen(fila.origen)}
                          className="mt-0.5 px-1.5 py-0 text-[10px]"
                        >
                          {nombreDelOrigen(fila.origen)}
                        </Badge>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">{fila.documento ?? ''}</p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.producto ?? ''}>
                          {fila.producto ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {formatCurrency(fila.precio ?? 0)} / u.
                        </p>
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        <p className="truncate">{formatNumber(fila.cantidad ?? 0)}</p>
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        <p className="truncate font-semibold text-ink-900">
                          {formatCurrency(fila.total ?? 0)}
                        </p>
                        <p
                          className="truncate text-[11px] text-ink-500"
                          title={`Base ${formatNumber(fila.base ?? 0)} · IGV ${formatNumber(fila.igv ?? 0)}`}
                        >
                          B {formatNumber(fila.base ?? 0)} · I {formatNumber(fila.igv ?? 0)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-600" title={fila.metodo_pago ?? ''}>
                          {fila.metodo_pago ?? '—'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {documento.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={4}>
                        SUMA · {formatNumber(resumen?.documentos ?? 0)} documento(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatNumber(resumen?.cantidad ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        <p className="truncate">{formatCurrency(resumen?.total ?? 0)}</p>
                        <p
                          className="truncate text-[11px] font-normal text-ink-500"
                          title={`Base ${formatNumber(resumen?.base ?? 0)} · IGV ${formatNumber(resumen?.igv ?? 0)}`}
                        >
                          B {formatNumber(resumen?.base ?? 0)} · I {formatNumber(resumen?.igv ?? 0)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2" />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="líneas" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Cada fila es una línea del comprobante (con su base «B» e IGV «I» debajo del total) y el pie
        suma todo el mes; la exportación trae el mismo listado en CSV con los totales. Los
        comprobantes anulados no se incluyen, como en el panel anterior.
      </p>
    </div>
  );
}
